import React, { useEffect, useRef, useState } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import mountainImage from "../Images/mountainimage4.jpg";
import axios from "axios";
import { KeyUri } from "../key";

// One deduped request per session; same endpoint + validity rules as PropertyDetail's coupon modal.
let couponsPromise = null;
const fetchHotelCouponsCached = () => {
  if (!couponsPromise) {
    couponsPromise = axios
      .get(`${KeyUri.BACKEND_URI}/hotel-coupons/public`)
      .then((res) => {
        const raw = res.data?.coupons || res.data?.data || res.data || [];
        return Array.isArray(raw) ? raw : [];
      })
      .catch((err) => { couponsPromise = null; throw err; });
  }
  return couponsPromise;
};
const isCouponLive = (c, now = new Date()) => {
  if (!c || !c.is_active) return false;
  if (c.valid_from && new Date(c.valid_from) > now) return false;
  if (c.valid_until && new Date(c.valid_until) < now) return false;
  if (c.usage_limit && c.used_count >= c.usage_limit) return false;
  return true;
};

const MAX_CARDS = 12;

// Ticket edge: one big semicircle at the middle + a column of small scallops
// above/below it, on both left and right edges (true transparent cut-outs).
const BIG_NOTCH = "20px";     // radius of the big middle notch
const SCALLOP = "4.5px";      // radius of the small scallops
const SCALLOP_ROWS = 11;      // keep ODD so the middle one lines up with the big notch
const cut = (r, x, size = "100% 100%") =>
  `radial-gradient(circle ${r} at ${x} 50%, #0000 97%, #000) 0 0 / ${size}`;
const maskValue = [
  cut(BIG_NOTCH, "0"),
  cut(BIG_NOTCH, "100%"),
  `${cut(SCALLOP, "0", `100% calc(100% / ${SCALLOP_ROWS})`)} repeat-y`,
  `${cut(SCALLOP, "100%", `100% calc(100% / ${SCALLOP_ROWS})`)} repeat-y`,
].join(", ");
const TICKET_MASK = {
  WebkitMask: maskValue,
  WebkitMaskComposite: "source-in",
  mask: maskValue,
  maskComposite: "intersect",
};
const FALLBACK_IMAGE = mountainImage; // used when a coupon has no image_url (swap for a hotel photo if you like)

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata" });

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch (e2) {
      return false;
    }
  }
};

/* ─────────────────────────────────────────────────────────────
   CouponCard — compact image voucher. Restyle everything here.
   Coupon fields used: coupon_code, coupon_amount, min_order_value,
   valid_until, applicable_hotels, and optional image_url / image.
   ───────────────────────────────────────────────────────────── */
const CouponCard = ({ coupon, copied, onCopy }) => {
  const amount = Number(coupon.coupon_amount || 0);
  const minOrder = Number(coupon.min_order_value || 0);
  const selectedHotelsOnly = coupon.applicable_hotels?.length > 0;
  const image = coupon.image_url || coupon.image || FALLBACK_IMAGE;

  const scope = selectedHotelsOnly ? "Selected Hotels" : "All Hotels";
  const discount = amount > 0 ? `Save ₹${amount.toLocaleString("en-IN")}` : "Special Offer";
  const terms = [
    minOrder > 0 ? `Min. Booking ₹${minOrder.toLocaleString("en-IN")}` : "No Min. Booking Value",
    coupon.valid_until ? `Valid Till ${fmtDate(coupon.valid_until)}` : null,
  ].filter(Boolean).join(" | ");

  return (
    <div className="shrink-0 w-[300px] sm:w-[400px] h-[150px] drop-shadow-md">
    <div className="relative group overflow-hidden rounded-xl w-full h-full" style={TICKET_MASK}>
      {/* Background image + gradient */}
      <img
        src={image}
        alt={scope}
        loading="lazy"
        onError={(e) => { if (e.currentTarget.src !== FALLBACK_IMAGE) e.currentTarget.src = FALLBACK_IMAGE; }}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/45 to-black/25" />

      {/* Content */}
      <div className="relative z-10 h-full px-6 py-4 flex flex-col text-white">
        <p className="text-xs text-white/80">Hotel Vouchers</p>
        <span className="absolute top-3 right-5 rounded border border-white/60 bg-white/10 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider">
          Exclusive
        </span>

        <h4 className="mt-1 text-lg sm:text-xl font-extrabold leading-tight">
          <span className="uppercase">{scope}:</span> {discount}
        </h4>
        <p className="mt-0.5 text-[11px] sm:text-xs text-white/85">{terms}</p>

        <div className="mt-auto flex items-center justify-between rounded-md bg-white p-1 pl-3 w-full max-w-[250px]">
          <span className="text-sm font-semibold tracking-wide text-slate-800 truncate">{coupon.coupon_code}</span>
          <button
            type="button"
            onClick={onCopy}
            className="shrink-0 rounded bg-[#1B2A4E] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#13203d] transition"
          >
            {copied ? "Copied ✓" : "Copy Code"}
          </button>
        </div>
      </div>
    </div>
    </div>
  );
};

/* Section shell: heading + scroll row + data. Renders nothing when
   there are no live coupons (same behaviour as LastMinuteEscapes). */
function HotelCouponsSection() {
  const [coupons, setCoupons] = useState(null);
  const [copiedCode, setCopiedCode] = useState("");
  const scrollRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    let alive = true;
    fetchHotelCouponsCached()
      .then((list) => {
        if (!alive) return;
        const live = list
          .filter((c) => c.coupon_code && isCouponLive(c))
          .sort((a, b) => (b.coupon_amount || 0) - (a.coupon_amount || 0))
          .slice(0, MAX_CARDS);
        setCoupons(live);
      })
      .catch(() => alive && setCoupons([]));
    return () => { alive = false; clearTimeout(timerRef.current); };
  }, []);

  if (!coupons || coupons.length === 0) return null;

  const handleCopy = async (code) => {
    if (await copyText(code)) {
      setCopiedCode(code);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopiedCode(""), 2000);
    }
  };
  const scrollBy = (dx) => scrollRef.current?.scrollBy({ left: dx, behavior: "smooth" });

  return (
    <section className="bg-gradient-to-br from-[#0B2A5B] via-[#12397A] to-[#1D4ED8]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between mb-5">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-sky-300 mb-1">Offers &amp; coupons</p>
            <h3 className="text-xl sm:text-2xl font-extrabold text-white">Save more on your next stay</h3>
            <p className="text-xs sm:text-sm text-blue-100/80 mt-1">Copy a code and apply it at checkout.</p>
          </div>
          <div className="hidden md:flex gap-2">
            <button type="button" aria-label="Scroll left" onClick={() => scrollBy(-300)} className="w-9 h-9 rounded-full bg-white/10 border border-white/30 text-white hover:bg-white hover:text-blue-700 hover:border-white flex items-center justify-center transition"><ChevronLeftIcon fontSize="small" /></button>
            <button type="button" aria-label="Scroll right" onClick={() => scrollBy(300)} className="w-9 h-9 rounded-full bg-white/10 border border-white/30 text-white hover:bg-white hover:text-blue-700 hover:border-white flex items-center justify-center transition"><ChevronRightIcon fontSize="small" /></button>
          </div>
        </div>

        <div ref={scrollRef} className="flex gap-4 overflow-x-auto pb-2 overscroll-x-contain" style={{ scrollbarWidth: "none", touchAction: "pan-x pan-y", WebkitOverflowScrolling: "touch" }}>
          {coupons.map((c) => (
            <CouponCard
              key={c._id || c.coupon_code}
              coupon={c}
              copied={copiedCode === c.coupon_code}
              onCopy={() => handleCopy(c.coupon_code)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export default React.memo(HotelCouponsSection);